import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { imgUrl, isBoosted } from "@/lib/api";
import { startCheckout } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import { Eye, MessageSquare, Package, CheckCircle2, Trash2, Pause, Play, Rocket, CreditCard, AlertCircle, Gavel, Pencil, Zap } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { isService, listingPath, formatRate } from "@/lib/services";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);

  const load = async () => {
    const [s, p] = await Promise.all([
      api.get("/stats"),
      api.get("/products", { params: { seller_id: user.id, status: "" } }),
    ]);
    setStats(s.data);
    setProducts(p.data);
  };

  useEffect(() => { if (user) load(); }, [user]);

  const drafts = products.filter((p) => p.status === "draft");

  const pay = async (lookup_key, id) => {
    try {
      await startCheckout(lookup_key, id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon de betaling niet starten. Probeer het opnieuw.");
    }
  };

  const boostDaysLeft = (p) => {
    if (!p.boosted_until) return null;
    const ms = new Date(p.boosted_until) - new Date();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const BoostMenu = ({ p, label }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`boost-${p.id}`} title={label} className="flex items-center gap-1.5 rounded-lg border border-amber-700/50 bg-amber-950/20 text-amber-300 hover:bg-amber-950/50 text-xs font-medium px-3 py-2 transition-colors">
          <Rocket className="h-3.5 w-3.5" /> {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-950 border-zinc-800 text-zinc-200 w-52">
        <DropdownMenuLabel className="text-zinc-500 text-xs">{label === "Verleng" ? "Verleng je boost" : "Kies een boost-pakket"}</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem data-testid={`boost-3d-${p.id}`} onClick={() => pay("boost_fee", p.id)} className="cursor-pointer focus:bg-zinc-800 flex justify-between">
          <span>3 dagen</span><span className="font-mono text-rose-400">€0,90</span>
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`boost-7d-${p.id}`} onClick={() => pay("boost_7d", p.id)} className="cursor-pointer focus:bg-zinc-800 flex justify-between">
          <span>7 dagen</span><span className="font-mono text-rose-400">€2,49</span>
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`boost-30d-${p.id}`} onClick={() => pay("boost_30d", p.id)} className="cursor-pointer focus:bg-zinc-800 flex justify-between">
          <span>30 dagen</span><span className="font-mono text-rose-400">€6,99</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const setStatus = async (id, status) => {
    await api.patch(`/products/${id}/status`, null, { params: { status } });
    toast.success("Status bijgewerkt");
    load();
  };

  const toggleToday = async (p) => {
    try {
      await api.post(`/products/${p.id}/available-today`, null, { params: { on: !p.manual_available_today } });
      toast.success(p.manual_available_today ? "'Vandaag beschikbaar' uitgezet" : "Je staat tot middernacht als 'Vandaag beschikbaar' bovenaan!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon status niet wijzigen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Weet je zeker dat je deze advertentie definitief wilt verwijderen? Bijbehorende biedingen en gesprekken worden ook verwijderd.")) return;
    await api.delete(`/products/${id}`);
    toast.success("Advertentie verwijderd");
    load();
  };

  const statItems = [
    { label: "Actieve advertenties", value: stats?.active ?? 0, icon: Package },
    { label: "Totaal weergaven", value: stats?.total_views ?? 0, icon: Eye },
    { label: "Gesprekken", value: stats?.messages ?? 0, icon: MessageSquare },
    { label: "Verkocht", value: stats?.sold ?? 0, icon: CheckCircle2 },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-1">Mijn dashboard</h1>
      <p className="text-sm text-zinc-500 mb-8">Beheer je advertenties en bekijk je statistieken.</p>

      {drafts.length > 0 && (
        <div data-testid="unpaid-reminder-banner" className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-800/50 bg-amber-950/20 px-5 py-4 animate-fade-up">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-400 shrink-0" />
            <span className="text-sm text-amber-100">
              Je hebt <span className="font-semibold">{drafts.length}</span> concept-advertentie{drafts.length > 1 ? "s" : ""} die nog <span className="font-semibold">€1,00</span> nodig {drafts.length > 1 ? "hebben" : "heeft"} om live te gaan.
            </span>
          </div>
          <button
            data-testid="pay-first-draft-button"
            onClick={() => pay("listing_fee", drafts[0].id)}
            className="shrink-0 flex items-center justify-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 text-sm font-medium px-4 py-2 transition-colors"
          >
            <CreditCard className="h-4 w-4" /> Publiceer nu (€1)
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {statItems.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <s.icon className="h-5 w-5 text-rose-400 mb-3" />
            <div className="font-mono text-3xl font-semibold text-zinc-100">{s.value}</div>
            <div className="text-xs text-zinc-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-zinc-200">Mijn advertenties</h2>
        <div className="flex gap-4">
          <Link to="/plaats" className="text-sm text-rose-400 hover:text-rose-300">+ Nieuw product</Link>
          <Link to="/dienst-plaatsen" data-testid="dashboard-new-service-link" className="text-sm text-rose-400 hover:text-rose-300">+ Nieuwe dienst</Link>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-zinc-800 bg-zinc-900/40 text-zinc-500">
          Je hebt nog geen advertenties. <Link to="/plaats" className="text-rose-400">Plaats je eerste product</Link>.
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`dashboard-listing-${p.id}`} className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-xl border bg-zinc-900/40 p-3 ${p.bid_count > 0 ? "border-rose-800/60 ring-1 ring-rose-900/40" : "border-zinc-800"}`}>
              <div className="flex items-center gap-4 min-w-0 w-full sm:flex-1">
                <Link to={listingPath(p)} className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-zinc-800">
                  {p.images?.length ? <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" /> : null}
                </Link>
                <div className="min-w-0 flex-1">
                  <Link to={listingPath(p)} className="text-sm font-medium text-zinc-200 truncate block hover:text-rose-300">
                    {isService(p) && <span data-testid={`dashboard-service-tag-${p.id}`} className="mr-2 rounded bg-rose-950/60 px-1.5 py-0.5 text-[10px] uppercase text-rose-300">Dienst</span>}
                    {p.title}
                  </Link>
                  <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-1">
                    <span className="font-mono text-rose-400">{isService(p) ? formatRate(p) : `€ ${Number(p.price).toFixed(2)}`}</span>
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {p.views}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${p.status === "active" ? "bg-emerald-950/50 text-emerald-400" : p.status === "sold" ? "bg-rose-950/50 text-rose-400" : p.status === "draft" ? "bg-amber-950/50 text-amber-400" : "bg-zinc-800 text-zinc-400"}`}>
                      {p.status === "active" ? "Actief" : p.status === "sold" ? "Verkocht" : p.status === "draft" ? "Niet betaald" : "Gepauzeerd"}
                    </span>
                    {isBoosted(p) && (
                      <span className="rounded-full px-2 py-0.5 text-[10px] uppercase bg-amber-500/20 text-amber-300 flex items-center gap-1">
                        <Rocket className="h-2.5 w-2.5" /> Uitgelicht{boostDaysLeft(p) != null ? ` · nog ${boostDaysLeft(p)}d` : ""}
                      </span>
                    )}
                    {p.bid_count > 0 && (
                      <Link to={`/product/${p.id}`} data-testid={`dashboard-bids-${p.id}`} className="rounded-full px-2.5 py-0.5 text-[10px] uppercase font-semibold bg-rose-600 text-white flex items-center gap-1 hover:bg-rose-500">
                        <Gavel className="h-2.5 w-2.5" /> {p.bid_count} {p.bid_count === 1 ? "bod" : "biedingen"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto shrink-0 justify-start sm:justify-end">
                {p.status === "draft" && (
                  <button data-testid={`publish-${p.id}`} onClick={() => pay("listing_fee", p.id)} className="flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-medium px-3 py-2 transition-colors">
                    <CreditCard className="h-3.5 w-3.5" /> Publiceer €1
                  </button>
                )}
                {isService(p) && p.status === "active" && (
                  <button data-testid={`available-today-toggle-${p.id}`} aria-pressed={!!p.manual_available_today} onClick={() => toggleToday(p)}
                    className={`flex items-center gap-1.5 rounded-lg border text-xs font-medium px-3 py-2 transition-colors ${p.manual_available_today ? "border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-500" : "border-emerald-800/60 bg-emerald-950/20 text-emerald-300 hover:bg-emerald-950/50"}`}>
                    <Zap className="h-3.5 w-3.5" /> {p.manual_available_today ? "Vandaag beschikbaar ✓" : "Vandaag beschikbaar"}
                  </button>
                )}
                {p.status === "active" && !isBoosted(p) && <BoostMenu p={p} label="Boost" />}
                {p.status === "active" && isBoosted(p) && <BoostMenu p={p} label="Verleng" />}
                <button data-testid={`edit-listing-${p.id}`} onClick={() => navigate(isService(p) ? `/dienst-plaatsen/${p.id}` : `/plaats/${p.id}`)} className="flex items-center gap-1.5 rounded-lg border border-zinc-700 text-zinc-200 hover:bg-zinc-800 text-xs font-medium px-3 py-2 transition-colors">
                  <Pencil className="h-3.5 w-3.5" /> Bewerken
                </button>
                {!isService(p) && p.status !== "sold" && p.status !== "draft" && (
                  <button data-testid={`mark-sold-${p.id}`} onClick={() => setStatus(p.id, "sold")} className="flex items-center gap-1.5 rounded-lg border border-emerald-800/60 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/60 text-xs font-medium px-3 py-2 transition-colors">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Verkocht
                  </button>
                )}
                {p.status === "active" && (
                  <button data-testid={`pause-${p.id}`} onClick={() => setStatus(p.id, "paused")} className="flex items-center gap-1.5 rounded-lg border border-amber-800/60 bg-amber-950/30 text-amber-300 hover:bg-amber-950/60 text-xs font-medium px-3 py-2 transition-colors">
                    <Pause className="h-3.5 w-3.5" /> Pauzeren
                  </button>
                )}
                {p.status === "paused" && (
                  <button data-testid={`activate-${p.id}`} onClick={() => setStatus(p.id, "active")} className="flex items-center gap-1.5 rounded-lg border border-emerald-800/60 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/60 text-xs font-medium px-3 py-2 transition-colors">
                    <Play className="h-3.5 w-3.5" /> Activeren
                  </button>
                )}
                <button data-testid={`delete-listing-${p.id}`} onClick={() => remove(p.id)} className="flex items-center gap-1.5 rounded-lg border border-rose-900/60 bg-rose-950/20 text-rose-300 hover:bg-rose-950/50 text-xs font-medium px-3 py-2 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" /> Verwijderen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
